// /tours — artist's saved tour routes (Wave B return-hooks).
//
// Lists every route the signed-in artist has saved, with a quick view of the
// cities + window + active state. Each row deep-links into /tours/$id where
// the live match preview lives. New-route CTA in the PageHero.

import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { EmptyState, MotionSection, PageHero } from '../../components/ui';
import { ApiError, fetchOk } from '../../lib/api';
import { csrfHeaders } from '../../lib/auth-context';
import { formatDateRangeMs } from '../../lib/date-format';
import { showToast } from '../../lib/toast';

export const Route = createFileRoute('/tours/')({
  component: ToursIndexPage,
});

type TourRouteCityLeg = {
  name: string;
  fromDate?: number;
  toDate?: number;
};

type TourRouteDTO = {
  id: string;
  name: string;
  cities: TourRouteCityLeg[];
  fromDate: number;
  toDate: number;
  isActive: boolean;
  notifyOnMatch: boolean;
  corridorRadiusKm: number;
  createdAt: number;
  updatedAt: number;
};

function ToursIndexPage(): React.JSX.Element {
  const [items, setItems] = useState<TourRouteDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchOk('/api/me/tour-routes', { credentials: 'include' });
        const json = (await res.json()) as { items: TourRouteDTO[] };
        if (cancelled) return;
        setItems(json.items);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "We couldn't load your routes.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleDelete(id: string): Promise<void> {
    setDeleting(true);
    try {
      await fetchOk(`/api/me/tour-routes/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: csrfHeaders(),
      });
      setItems((prev) => (prev ?? []).filter((r) => r.id !== id));
      showToast({ kind: 'success', message: 'Route removed.' });
      setConfirmId(null);
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : "We couldn't remove that route.",
      });
    } finally {
      setDeleting(false);
    }
  }

  async function handleToggleActive(route: TourRouteDTO): Promise<void> {
    setTogglingId(route.id);
    try {
      const res = await fetchOk(
        `/api/me/tour-routes/${encodeURIComponent(route.id)}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify({ is_active: !route.isActive }),
        },
      );
      const json = (await res.json()) as { item: TourRouteDTO };
      setItems((prev) =>
        (prev ?? []).map((r) => (r.id === route.id ? json.item : r)),
      );
      showToast({
        kind: 'success',
        message: json.item.isActive ? 'Route active.' : 'Route paused.',
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        showToast({
          kind: 'warning',
          message: 'This route changed in another tab. Refreshing.',
        });
        try {
          const res = await fetchOk('/api/me/tour-routes', {
            credentials: 'include',
          });
          const j = (await res.json()) as { items: TourRouteDTO[] };
          setItems(j.items);
        } catch {
          /* keep the existing list on a refresh failure */
        }
      } else {
        showToast({
          kind: 'error',
          message:
            err instanceof ApiError ? err.message : "We couldn't update that.",
        });
      }
    } finally {
      setTogglingId(null);
    }
  }

  const confirmTarget = items?.find((r) => r.id === confirmId) ?? null;

  return (
    <AppShell tone="content">
      <Stack gap="lg">
        <PageHero
          eyebrow="Tour routes"
          title="Plan where you'd like to be"
          lede="Save a city list and date range. We'll let you know when a venue or host opens up that fits."
          action={
            <Button
              component={Link}
              to="/tours/new"
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
            >
              New route
            </Button>
          }
        />

        {error !== null ? (
          <Text c="red" role="alert">
            {error}
          </Text>
        ) : items === null ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : items.length === 0 ? (
          <MotionSection>
            <EmptyState
              title="Save your first route."
              body="Cities you'd love to play, dates you can travel. We'll keep an eye out."
              action={
                <Button
                  component={Link}
                  to="/tours/new"
                  variant="gradient"
                  gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                >
                  Plan a route
                </Button>
              }
            />
          </MotionSection>
        ) : (
          <MotionSection>
            <Stack gap="md">
              {items.map((route) => (
                <RouteCard
                  key={route.id}
                  route={route}
                  toggling={togglingId === route.id}
                  onToggle={() => void handleToggleActive(route)}
                  onDelete={(id) => setConfirmId(id)}
                />
              ))}
            </Stack>
          </MotionSection>
        )}
      </Stack>

      <Modal
        opened={confirmTarget !== null}
        onClose={() => (deleting ? undefined : setConfirmId(null))}
        title="Remove this route?"
        centered
      >
        <Stack gap="md">
          <Text>
            {confirmTarget !== null
              ? `"${confirmTarget.name}" will stop sending match notifications.`
              : ''}
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => setConfirmId(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleting}
              onClick={() => {
                if (confirmTarget !== null) {
                  void handleDelete(confirmTarget.id);
                }
              }}
            >
              Remove route
            </Button>
          </Group>
        </Stack>
      </Modal>
    </AppShell>
  );
}

function RouteCard({
  route,
  toggling,
  onToggle,
  onDelete,
}: {
  route: TourRouteDTO;
  toggling: boolean;
  onToggle: () => void;
  onDelete: (id: string) => void;
}): React.JSX.Element {
  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" align="center" wrap="wrap">
              <Text fw={600} size="lg">
                {route.name}
              </Text>
              <Badge
                variant="light"
                color={route.isActive ? 'tide' : 'gray'}
                size="sm"
              >
                {route.isActive ? 'Active' : 'Paused'}
              </Badge>
            </Group>
            <Text c="dimmed" size="sm">
              {formatDateRangeMs(route.fromDate, route.toDate)}
            </Text>
          </Stack>
        </Group>

        <Group gap={6} wrap="wrap">
          {route.cities.map((leg) => (
            <Badge key={leg.name} variant="outline" tt="capitalize">
              {leg.name}
            </Badge>
          ))}
        </Group>

        {route.notifyOnMatch ? (
          <Text size="xs" c="dimmed">
            Notifications on. We'll let you know when a venue or open week fits.
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            Notifications off. Open the route to see live matches.
          </Text>
        )}

        <Group justify="space-between" wrap="wrap" gap="sm">
          <Anchor
            component="button"
            type="button"
            size="sm"
            c="red"
            onClick={() => onDelete(route.id)}
          >
            Remove
          </Anchor>
          <Group gap="xs" wrap="wrap">
            <Link
              to="/tours/$id/edit"
              params={{ id: route.id }}
              style={{ textDecoration: 'none' }}
            >
              <Button component="span" tabIndex={-1} variant="subtle" size="sm">
                Edit
              </Button>
            </Link>
            <Button
              variant="subtle"
              size="sm"
              loading={toggling}
              onClick={onToggle}
            >
              {route.isActive ? 'Pause' : 'Resume'}
            </Button>
            <Link
              to="/tours/$id"
              params={{ id: route.id }}
              style={{ textDecoration: 'none' }}
            >
              <Button component="span" tabIndex={-1} variant="light" size="sm">
                View matches
              </Button>
            </Link>
          </Group>
        </Group>
      </Stack>
    </Card>
  );
}
